// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { DataTableCell } from "@insite/mobius/index";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLocalizeCurrency from "@store/Framework/Common/Hooks/useLocalizeCurrency";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import { InvoiceDetailModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { isInvoiceDetail } from "@store/Framework/Services/Reporting/ReportingTypeGuards";
import { InvoiceDetailParameters, InvoiceSummaryData } from "@store/Framework/Types/types";
import ReportDataTable, {
    DataTableData,
    FormatDictionary,
} from "@store/Library/Components/Reporting/ApiDataTable/ReportDataTable";
import { ButtonReport } from "@store/Library/Components/Reporting/Common/ApiDataModal";
import InvoiceDetailModal from "@store/Library/Components/Reporting/InvoiceDetail/InvoiceDetailModal";
import { BudgetDetailsData } from "@store/Library/Widgets/Reports/BudgetDetailReport";
import { invoiceSummaryHeaders, invoiceSummaryKeys } from "@store/Library/Widgets/Reports/InvoiceDetailSearchForm";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

interface BudgetDetailTableProps {
    budget: BudgetDetailsData;
    show: boolean;
}
interface BudgetDetailTableStyles {
    companyCard?: InjectableCss;
    companyCardTypography?: TypographyProps;
    tableCellStyles?: InjectableCss;
}

const BudgetDetailTableStyles: BudgetDetailTableStyles = {
    tableCellStyles: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    companyCard: {
        css: css`
            ${breakpointValues(["column", "row"], "flex-direction")}
            ${breakpointValues(["1rem", "initial"], "row-gap")}
            margin-top: ${pxToRem(24)};
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: ${getColor("primary")};
            color: white;
            align-self: center;
            border-radius: ${pxToRem(8)};
            padding: ${pxToRem(32)};
        `,
    },
    companyCardTypography: {
        css: css`
            color: white;
            ${breakpointValues(["inherit", "center", "inherit"], "text-align")}
        `,
    },
};
const styles = BudgetDetailTableStyles;
const BudgetDetailTable: React.FC<BudgetDetailTableProps> = ({ budget, show }) => {
    const { isLoading, updateData } = useReportApi("invoice");

    const [openModel, setOpenModel] = React.useState<number>();
    const [invoiceDetails, setInvoiceDetails] = React.useState<
        Record<number, [InvoiceSummaryData, InvoiceDetailModel]>
    >({});
    const localizeCurrency = useLocalizeCurrency();
    if (!show) {
        return null;
    }
    const { title, startDate, endDate, limit, invoices } = budget;
    const totalExpentitures = invoices.reduce((acc, invoice) => {
        return acc + Number(invoice.ordertotal);
    }, 0);
    const budgetBalance = limit - totalExpentitures;

    const formatDictionary: FormatDictionary = {
        itemstotal: localizeCurrency,
        gst: localizeCurrency,
        freightcharges: localizeCurrency,
        ordertotal: localizeCurrency,
    };

    const handleModalOpen = async (invoiceSummary: InvoiceSummaryData) => {
        const invoiceNum = invoiceSummary.invoicenum;
        const invoiceDetailParameters: InvoiceDetailParameters = {
            invoiceNum,
            billableTreatment: "all",
            sourceType: "invoice",
        };
        setOpenModel(invoiceNum);
        if (invoiceDetails[invoiceNum] !== undefined) {
            return;
        }

        const data = await updateData(invoiceDetailParameters);
        if (isInvoiceDetail(data)) {
            setInvoiceDetails(prevState => ({ ...prevState, [invoiceNum]: [invoiceSummary, data] }));
        }
    };
    const dataWithElements = invoices.map(invoice => {
        const invoiceNum = invoice.invoicenum;
        const element = (
            <DataTableCell key={invoice.invoicenum} {...styles.tableCellStyles}>
                <ButtonReport
                    isLoading={isLoading && openModel === invoiceNum}
                    onClick={() => handleModalOpen(invoice)}
                >
                    {translate("View Order Items")}
                </ButtonReport>
            </DataTableCell>
        );
        return { ...invoice, extraElement: element };
    });
    const tableTitle = translate("{0} Invoice Summaries", title);
    const tableData: DataTableData<InvoiceSummaryData> = {
        data: dataWithElements,
        headers: invoiceSummaryHeaders,
        dataKeys: invoiceSummaryKeys,
        title: tableTitle,
        dataSummaryTitle: translate("Invoices"),
        formatDictionary,
    };

    const excelData = invoices.map(invoice => {
        return { ...invoice, budget: limit, startDate, endDate, totalExpentitures, budgetBalance };
    });

    const excelKeys = [...invoiceSummaryKeys, "budget", "startDate", "endDate", "totalExpentitures", "budgetBalance"];
    const excelHeaders = [
        ...invoiceSummaryHeaders,
        translate("Budget"),
        translate("Start Date"),
        translate("End Date"),
        translate("Total Expenditures"),
        translate("Budget Balance"),
    ];

    const exceltableData: DataTableData<any> = {
        data: excelData,
        headers: excelHeaders,
        dataKeys: excelKeys,
        title: tableTitle,
        dataSummaryTitle: translate("Invoices"),
        formatDictionary,
    };
    const activeInvoice = openModel ? invoiceDetails[openModel] : undefined;
    const [activeInvoiceSummary, activeInvoiceDetail] = activeInvoice || [undefined, undefined];
    const detailHeaders = [
        title,
        `${startDate} - ${endDate}`,
        translate("Budget: {0}", localizeCurrency(limit)),
        translate("Expenditures: {0}", localizeCurrency(totalExpentitures)),
        translate("Remaining: {0}", localizeCurrency(budgetBalance)),
    ];
    return (
        <>
            <InvoiceDetailModal
                invoiceDetails={activeInvoiceDetail}
                invoiceSummary={activeInvoiceSummary}
                isOpen={!!openModel}
                invoiceSummaryDictionary={formatDictionary}
                handleClose={() => setOpenModel(undefined)}
            />
            <StyledWrapper {...styles.companyCard}>
                {detailHeaders.map(header => (
                    <Typography {...styles.companyCardTypography} key={header}>
                        {header}
                    </Typography>
                ))}
            </StyledWrapper>
            <ReportDataTable excelData={exceltableData} dataTableData={tableData} />
        </>
    );
};

export default BudgetDetailTable;
